type PaymentFields = {
  status?: string | null;
  paymentAmountCents?: number | null;
  depositAmountCents?: number | null;
  stripePaymentIntentId?: string | null;
  sharedPaymentIntentId?: string | null;
  depositStripePaymentIntentId?: string | null;
  depositStripePaymentIntentStatus?: string | null;
  sharedDepositPaymentIntentId?: string | null;
  sharedDepositPaymentIntentStatus?: string | null;
  sharedPaymentIntentStatus?: string | null;
  stripePaymentIntentStatus?: string | null;
  balanceCronExcluded?: boolean | null;
};

export type PaymentLinkKind = "deposit" | "balance" | "full";

export function hasApprovedBalancePayment(participant: PaymentFields): boolean {
  return (
    participant.status === "APPROVED" &&
    (Boolean(participant.stripePaymentIntentId) ||
      Boolean(participant.sharedPaymentIntentId))
  );
}

export function hasApprovedDepositPayment(participant: PaymentFields): boolean {
  return (
    participant.status === "APPROVED" &&
    ((Boolean(participant.depositStripePaymentIntentId) &&
      participant.depositStripePaymentIntentStatus === "succeeded") ||
      (Boolean(participant.sharedDepositPaymentIntentId) &&
        participant.sharedDepositPaymentIntentStatus === "succeeded"))
  );
}

export function hasApprovedPaymentCaptured(participant: PaymentFields): boolean {
  return (
    hasApprovedBalancePayment(participant) ||
    hasApprovedDepositPayment(participant)
  );
}

export function isEligibleForSharedBalanceCollection(
  participant: PaymentFields & {
    boatId?: string | null;
  },
): boolean {
  return (
    participant.status === "APPROVED" &&
    participant.boatId == null &&
    participant.sharedDepositPaymentIntentStatus === "succeeded" &&
    !participant.sharedPaymentIntentId &&
    participant.stripePaymentIntentStatus !== "succeeded"
  );
}

export function isBalanceCronExcluded(participant: PaymentFields): boolean {
  return participant.balanceCronExcluded === true;
}

function hasDepositPaidFields(participant: PaymentFields): boolean {
  return (
    participant.depositStripePaymentIntentStatus === "succeeded" ||
    participant.sharedDepositPaymentIntentStatus === "succeeded"
  );
}

function hasBalanceOrFullPaidFields(participant: PaymentFields): boolean {
  return (
    participant.stripePaymentIntentId != null ||
    participant.stripePaymentIntentStatus === "succeeded" ||
    participant.sharedPaymentIntentId != null ||
    participant.sharedPaymentIntentStatus === "succeeded"
  );
}

/** Payment kinds that can be collected via a payment link for this participant. */
export function getApplicablePaymentLinkKinds(
  participant: PaymentFields,
): PaymentLinkKind[] {
  if (
    participant.status !== "PENDING" &&
    participant.status !== "APPROVED"
  ) {
    return [];
  }

  const kinds: PaymentLinkKind[] = [];
  const depositCents = participant.depositAmountCents ?? 0;
  const paymentCents = participant.paymentAmountCents ?? 0;
  const balanceCents = paymentCents - depositCents;
  const depositPaid = hasDepositPaidFields(participant);
  const balanceOrFullPaid = hasBalanceOrFullPaidFields(participant);

  if (depositCents > 0 && !depositPaid && !balanceOrFullPaid) {
    kinds.push("deposit");
  }
  if (depositPaid && balanceCents > 0 && !balanceOrFullPaid) {
    kinds.push("balance");
  }
  if (paymentCents > 0 && !depositPaid && !balanceOrFullPaid) {
    kinds.push("full");
  }

  return kinds;
}

/** Kinds applicable to every participant in the selection. */
export function getSharedApplicablePaymentLinkKinds(
  participants: PaymentFields[],
): PaymentLinkKind[] {
  if (participants.length === 0) return [];

  const [first, ...rest] = participants;
  const initial = new Set(getApplicablePaymentLinkKinds(first));
  for (const participant of rest) {
    const kinds = new Set(getApplicablePaymentLinkKinds(participant));
    for (const kind of [...initial]) {
      if (!kinds.has(kind)) {
        initial.delete(kind);
      }
    }
  }
  return (["deposit", "balance", "full"] as const).filter((kind) =>
    initial.has(kind),
  );
}
